import { differenceInSeconds } from "date-fns"
import { StorageSerializers, useSessionStorage } from "@vueuse/core"
import * as Sentry from "@sentry/vue"
import { defineStore } from "pinia"
import { sendReplayFeedback } from "@/logic/sendReplayFeedback"
import { getI18n } from "@/logic/i18n"
import { useAccountStore } from "@/modules/account/store"
import { useAppConfigStore } from "@/store/appConfig"
import { useAppProfileConfigStore } from "@/store/appProfileConfig"
import router from "@/router"
import { clearStoredReplaySession } from "@/logic/sentry"

export const MAX_REPLAY_DESCRIPTION_LENGTH = 4000
const RECORDING_SESSION_STORAGE_KEY = "manual-session-replay"
// Match the Sentry Replay SDK maximum session duration.
const REPLAY_MAX_DURATION_MS = 60 * 60 * 1000

interface RecordingSession {
  replayId: string
  startedAt: number
}

function isRecordingSessionExpired(session: RecordingSession, now: number): boolean {
  return now - session.startedAt >= REPLAY_MAX_DURATION_MS
}

interface ReplayRuntime {
  replay?: ReturnType<typeof Sentry.getReplay>
  interval?: ReturnType<typeof setInterval>
  removeListeners: Array<() => void>
}

let replayRuntime: ReplayRuntime | undefined

const useStore = defineStore("sessionReplay", {
  state: () => ({
    recordingSession: useSessionStorage<RecordingSession | null>(RECORDING_SESSION_STORAGE_KEY, null, {
      flush: "sync",
      listenToStorageChanges: false,
      serializer: StorageSerializers.object,
      onError: () => {},
    }),
    isStopping: false,
    error: undefined as "unavailable" | "startFailed" | undefined,
    result: undefined as { replayId: string; interrupted: boolean } | undefined,
    isStopDialogOpen: false,
    isStartDialogOpen: false,
    description: "",
    descriptionStatus: "idle" as "idle" | "sending" | "sent" | "failed",
    now: Date.now(),
    showResultAfterStop: true,
    feedbackEventId: undefined as string | undefined,
    submittedDescription: "",
  }),
  getters: {
    isRecording: (state) => !!state.recordingSession,
    activeReplayId: (state) => state.recordingSession?.replayId,
    isSendingDescription: (state) => state.descriptionStatus === "sending",
    // The SDK has no public start timestamp. Explicit-start time is saved per tab.
    elapsedSeconds: (state) => (state.recordingSession ? Math.max(0, differenceInSeconds(state.now, state.recordingSession.startedAt)) : 0),
  },
  actions: {
    initialize() {
      if (replayRuntime) {
        return
      }
      const runtime: ReplayRuntime = { removeListeners: [] }
      replayRuntime = runtime
      const listen = (eventName: string, listener: EventListener, options?: AddEventListenerOptions) => {
        window.addEventListener(eventName, listener, options)
        runtime.removeListeners.push(() => window.removeEventListener(eventName, listener, options))
      }
      listen("focus", () => this.syncRecording())
      listen("pageshow", (event) => {
        if ((event as PageTransitionEvent).persisted) {
          // A cached page can retain a recorder after its saved recording state was cleared elsewhere.
          try {
            if (!sessionStorage.getItem(RECORDING_SESSION_STORAGE_KEY)) {
              this.stopForLogout()
            }
          } catch {
            this.stopForLogout()
          }
          router.go(0)
          return
        }
        this.syncRecording()
      })
      const originalDispose = this.$dispose.bind(this)
      this.$dispose = () => {
        this.pauseTimer()
        runtime.removeListeners.forEach((removeListener) => removeListener())
        if (replayRuntime === runtime) {
          replayRuntime = undefined
        }
        originalDispose()
      }
      // Sampling is zero; only a recording started in this tab resumes.
      const previousSession = this.recordingSession
      if (previousSession) {
        if (isRecordingSessionExpired(previousSession, Date.now())) {
          this.recordingSession = null
          this.presentResult(previousSession.replayId, true)
        } else {
          void this.beginRecording(previousSession)
        }
      }
    },
    resumeTimer() {
      const runtime = replayRuntime!
      if (!runtime.interval) {
        runtime.interval = setInterval(() => this.syncRecording(), 1000)
      }
    },
    pauseTimer() {
      const runtime = replayRuntime
      if (runtime?.interval) {
        clearInterval(runtime.interval)
        runtime.interval = undefined
      }
    },
    presentResult(replayId: string, interrupted: boolean) {
      this.isStartDialogOpen = false
      if (!this.showResultAfterStop) {
        return
      }
      this.description = ""
      this.descriptionStatus = "idle"
      this.feedbackEventId = undefined
      this.submittedDescription = ""
      this.result = { replayId, interrupted }
      this.isStopDialogOpen = true
    },
    syncRecording() {
      const session = this.recordingSession
      if (!session) {
        return
      }
      this.now = Date.now()
      if (isRecordingSessionExpired(session, this.now)) {
        void this.stop(true)
        return
      }
      if (replayRuntime?.replay?.getReplayId() === session.replayId) {
        return
      }
      this.recordingSession = null
      this.presentResult(session.replayId, true)
      this.pauseTimer()
    },
    openStartDialog() {
      if (this.isRecording || this.isStopping || this.isSendingDescription) {
        return
      }
      if (this.result && this.descriptionStatus !== "sent") {
        this.isStopDialogOpen = true
        return
      }
      this.error = undefined
      this.isStartDialogOpen = true
    },
    discardReport() {
      if (this.isSendingDescription) {
        return
      }
      this.isStopDialogOpen = false
      this.result = undefined
      this.description = ""
      this.descriptionStatus = "idle"
    },
    async start() {
      await this.beginRecording(null)
    },
    async beginRecording(previousSession: RecordingSession | null) {
      if (this.isStopping || (this.isRecording && !previousSession) || this.isSendingDescription) {
        return
      }
      this.error = undefined
      this.result = undefined
      this.isStopDialogOpen = false
      this.showResultAfterStop = true
      const runtime = replayRuntime!
      runtime.replay = Sentry.getReplay()
      if (!runtime.replay) {
        this.recordingSession = null
        this.error = "unavailable"
        return
      }
      const startedAt = previousSession?.startedAt ?? Date.now()
      if (!previousSession) {
        clearStoredReplaySession()
      }
      runtime.replay.start()
      const replayId = runtime.replay.getReplayId()
      if (!replayId || (previousSession && replayId !== previousSession.replayId)) {
        this.recordingSession = null
        await runtime.replay.stop()
        this.error = "startFailed"
        return
      }
      this.recordingSession = { replayId, startedAt }
      this.now = Date.now()
      this.isStartDialogOpen = false
      this.resumeTimer()
    },
    async stop(interrupted = false) {
      this.isStartDialogOpen = false
      const session = this.recordingSession
      // Clear saved recording state before the final upload so a reload cannot restart it.
      this.recordingSession = null
      const runtime = replayRuntime!
      if (this.isStopping || !runtime.replay) {
        return
      }
      this.isStopping = true
      this.error = undefined
      const replayId = runtime.replay.getReplayId() ?? session?.replayId
      await runtime.replay.stop()
      this.isStopping = false
      this.pauseTimer()
      if (replayId) {
        this.presentResult(replayId, interrupted)
      }
    },
    stopForLogout() {
      this.showResultAfterStop = false
      this.isStopDialogOpen = false
      this.result = undefined
      this.description = ""
      this.descriptionStatus = "idle"
      this.error = undefined
      // Detach immediately; do not await network retries during logout.
      void this.stop()
    },
    async sendDescription() {
      if (!this.result || this.isSendingDescription || this.descriptionStatus === "sent" || this.description.length > MAX_REPLAY_DESCRIPTION_LENGTH) {
        return
      }
      const message = this.description.trim() || getI18n().t("sessionReplay.defaultDescription")
      const submittedResult = this.result
      this.descriptionStatus = "sending"
      try {
        if (!this.feedbackEventId || message !== this.submittedDescription) {
          this.feedbackEventId = crypto.randomUUID().replaceAll("-", "")
          this.submittedDescription = message
        }
        const accountStore = useAccountStore()
        const appConfigStore = useAppConfigStore()
        const appProfileConfigStore = useAppProfileConfigStore()
        await sendReplayFeedback(submittedResult.replayId, message, this.feedbackEventId, {
          user: { id: accountStore.id?.toString(), username: accountStore.username, email: accountStore.email },
          // A superadmin's login app can differ from the app selected when submitting.
          tags: { app_id: appConfigStore.appId!.toString(), app_name: appProfileConfigStore.settings.app_name },
        })
        if (this.result === submittedResult) {
          this.descriptionStatus = "sent"
          this.isStopDialogOpen = false
        }
      } catch {
        if (this.result === submittedResult) {
          this.descriptionStatus = "failed"
        }
      }
    },
  },
})

export function useSessionReplayStore() {
  const store = useStore()
  store.initialize()
  return store
}
